/**
 * Format a date as DD/MM/YYYY. Handles ISO date strings ("YYYY-MM-DD" or full
 * ISO datetimes) and returns undefined for null/empty/invalid input so callers
 * can render their own fallback.
 */
export function formatDDMMYYYY(value: string | number | null | undefined): string | undefined {
  if (value === null || value === undefined || value === "" || value === "0000-00-00") {
    return undefined;
  }
  const iso = String(value).slice(0, 10);
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (match) {
    const [, y, m, d] = match;
    return `${d}/${m}/${y}`;
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return undefined;
  const dd = String(parsed.getDate()).padStart(2, "0");
  const mm = String(parsed.getMonth() + 1).padStart(2, "0");
  return `${dd}/${mm}/${parsed.getFullYear()}`;
}

/**
 * Earliest permitted contract end date: one calendar month after the start date.
 * Moved VERBATIM out of the AddService closure (S5d) so the extracted contract-parameter
 * step components can be rendered outside the wizard — e.g. inside the Customer Switch
 * popup's ask-phase. Pure; no behaviour change.
 */
export const getMinEndDate = (start: string) => {
  if (!start) return "";
  const date = new Date(start);
  date.setMonth(date.getMonth() + 1);
  return date.toISOString().split("T")[0];
};

export function getFirstOfMonthOnOrAfter(dateStr: string): string {
  const d = new Date(dateStr);
  const year = d.getFullYear();
  const month = d.getMonth(); // 0-indexed
  if (d.getDate() === 1) return dateStr;
  const nextMonth = month + 1 > 11 ? 0 : month + 1;
  const nextYear = month + 1 > 11 ? year + 1 : year;
  return `${nextYear}-${String(nextMonth + 1).padStart(2, "0")}-01`;
}

/**
 * Today's LOCAL calendar date as "YYYY-MM-DD". Uses local date parts rather than
 * toISOString() (UTC), which for Europe/Brussels users in the hour(s) after local
 * midnight would still report the previous day and offer/accept a 1st already passed.
 */
export function getLocalTodayISO(): string {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

/** FT-5416: earliest permitted basic-contract start — today if today is the 1st, else the 1st of next month. */
export function getEarliestValidStartDate(): string {
  return getFirstOfMonthOnOrAfter(getLocalTodayISO());
}

/** FT-5416: a compliant basic-contract start date — the 1st of a month, today or later. */
export function isValidBasicStartDate(dateStr: string): boolean {
  if (!/^\d{4}-\d{2}-01$/.test(dateStr)) return false;
  return dateStr >= getLocalTodayISO();
}
